import { CustomAnswers } from './CustomQuestions';
import { WardCard } from './WardCard';
import { WishText } from '@src/components/WishText/WishText';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo, FullCardInfo } from '@lub0v/santa-utils';
import {
  Badge,
  Button,
  CardDetail,
  Menu,
  Link,
  ParticipantFace,
  Stack,
  Typography,
  WarningIcon,
  showDialog,
  showToast,
} from '@src/components';
import { CardDetailHeader } from '@src/components/CardDetail/CardDetail';
import { PersonCard } from '@src/components/PersonCard/PersonCard';
import './MyCardView.css';
import { cardIsFilled } from '@src/pages/BoxPage/full/cardStatus';

import { boxCardEditPath, boxExchangePath, boxMessengerPath } from '@src/routing/routes';
import { santaClient } from '@src/state/client';
export interface MyCardViewProps {
  info: BoxInfo;
  card: FullCardInfo;
  own?: CardInfo;
  onChanged: () => Promise<void> | void;
}

export function MyCardView({ info, card, own, onChanged }: MyCardViewProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [santaView, setSantaView] = useState(false);
  const archived = Boolean(info.box.isArchived);
  const received = Boolean(own?.isGiftReceived);
  const onTheWay = Boolean(own?.isGiftOnItsWay);
  const filled = cardIsFilled(card, info.box);
  const wishText = (card.wish || '').trim();
  const needsDelivery = Boolean(info.box.usePost || info.box.isPhoneRequired);
  const delivery = [
    info.box.usePost ? card.postalRecipient : '',
    info.box.usePost ? card.postalAddress : '',
    info.box.usePost ? card.postalCode : '',
    card.phone,
  ]
    .filter(Boolean)
    .join('  ·  ');

  async function markReceived() {
    setBusy(true);
    const res = await santaClient.writeToSanta({
      key: info.box.key,
      message: t('game.gift.receivedMessage'),
      isGiftReceived: true,
      isAutomatic: true,
    });
    setBusy(false);
    if (!res.ok) {
      showToast(t(res.error?.message || 'newBox.saveFailed'));
      return;
    }
    showToast(t('game.gift.receivedDone'), 'success');
    await onChanged();
  }

  async function undoReceived() {
    if (busy) return;
    setBusy(true);
    try {
      const result = await santaClient.getSantaMessages({ key: info.box.key });
      if (!result.ok) throw new Error(result.error?.message || 'newBox.saveFailed');
      const receipts = (result.data || []).filter(
        (message) => message.isYou && message.isGiftReceived && message.active !== false,
      );
      for (const message of receipts) {
        const deleted = await santaClient.updateOwnMessage({
          key: info.box.key,
          id: message.id,
          message: '',
        });
        if (!deleted.ok) throw new Error(deleted.error?.message || 'newBox.saveFailed');
      }
    } catch (error) {
      showToast(t(error instanceof Error ? error.message : 'newBox.saveFailed'));
    } finally {
      setBusy(false);
      await onChanged();
    }
  }

  function confirmReceived() {
    showDialog({
      title: t('game.gift.receivedSubmit'),
      children: t('game.gift.onTheWay'),
      cancel: t('box.cancel'),
      submit: t('game.gift.receivedSubmit'),
      onSubmit: markReceived,
    });
  }

  if (santaView) {
    return (
      <CardDetail className="ward-detail" testId="game-my-card-santa">
        <CardDetailHeader
          title={t('game.card.santaView')}
          backLabel={t('game.exchange.myCard')}
          onBack={() => setSantaView(false)}
          testId="game-card-back"
        />
        <WardCard info={info} ward={card} sent={onTheWay} preview />
      </CardDetail>
    );
  }

  return (
    <CardDetail className="my-card-detail" testId="game-my-card">
      <CardDetailHeader
        title={t('game.exchange.myCard')}
        backLabel={t('game.exchange.title')}
        onBack={() => navigate(boxExchangePath(info.box.key))}
        testId="game-card-back"
      />
      <PersonCard
        className="my-card-detail__sheet"
        avatar={<ParticipantFace card={card} size="identity" />}
        name={card.username}
        status={
          <Badge tone={filled ? 'success' : 'neutral'} className="my-card-detail__completion">
            {!filled && <WarningIcon />}
            {filled ? t('game.exchange.cardReady') : t('game.exchange.cardEmpty')}
          </Badge>
        }
        editAction={
          <div className="my-card-detail__menu">
            <Menu
              label={t('game.card.manage')}
              testId="game-card-menu"
              items={[
                {
                  id: 'edit',
                  label: t('game.card.edit'),
                  disabled: archived,
                  onSelect: () => navigate(boxCardEditPath(info.box.key)),
                  testId: 'game-card-edit',
                },
                {
                  id: 'preview',
                  label: t('game.card.santaView'),
                  onSelect: () => setSantaView(true),
                  testId: 'game-card-santa-view',
                },
                {
                  id: 'chat',
                  label: t('game.card.writeSanta'),
                  disabled: archived || !info.isActiveDraw,
                  onSelect: () => navigate(boxMessengerPath(info.box.key, 'me')),
                  testId: 'game-card-santa-chat',
                },
                ...(received
                  ? [
                      {
                        id: 'undo-received',
                        label: t('game.gift.notReceivedYet'),
                        disabled: archived || busy,
                        onSelect: () => void undoReceived(),
                        testId: 'game-card-undo-received',
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        }
      >
        {!card.customAnswers?.some((a) => a.field.type === 'santa_letter') && (
          <section className="card-sheet__section card-sheet__section--wish">
            <Typography as="h3" role="title/card">
              {t('game.card.wish')}
            </Typography>
            <p className="card-sheet__body">
              <WishText>
                {info.box.useWish
                  ? wishText || t('game.card.emptyWish')
                  : t('game.card.wishesDisabled')}
              </WishText>
              {info.box.useWish && !wishText && !archived && (
                <>
                  {' '}
                  <Link
                    variant="inline"
                    href={boxCardEditPath(info.box.key)}
                    testId="game-card-add-wish"
                  >
                    {t('game.card.addDetails')}
                  </Link>
                </>
              )}
            </p>
          </section>
        )}
        {needsDelivery &&
        !card.customAnswers?.some((a) => ['postal_address', 'delivery_address', 'phone'].includes(a.field.type)) ? (
          <section className="card-sheet__section">
            <Typography as="h3" role="title/card">
              {t('game.card.delivery')}
            </Typography>
            {delivery ? (
              <dl className="my-card-detail__delivery">
                {[
                  ['recipient', info.box.usePost ? card.postalRecipient : ''],
                  ['address', info.box.usePost ? card.postalAddress : ''],
                  ['code', info.box.usePost ? card.postalCode : ''],
                  ['phone', card.phone],
                ]
                  .filter(([, value]) => Boolean(value))
                  .map(([label, value]) => (
                    <div key={label}>
                      <dt>{t(`game.cardCreate.delivery.${label}`)}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
              </dl>
            ) : (
              <p className="card-sheet__body">
                {t('game.card.emptyDelivery')}{' '}
                {!archived && (
                  <Link
                    variant="inline"
                    href={boxCardEditPath(info.box.key)}
                    testId="game-card-edit-delivery"
                  >
                    {t('game.card.addDetails')}
                  </Link>
                )}
              </p>
            )}
          </section>
        ) : null}
        <CustomAnswers answers={card.customAnswers} />
        {!received ? (
          <Stack gap={3} className="my-card-detail__gift">
            <Button
              variant="primary"
              fullWidth
              loading={busy}
              disabled={!info.isActiveDraw || archived}
              onClick={confirmReceived}
              testId="game-card-received"
            >
              {t('game.gift.receivedSubmit')}
            </Button>
          </Stack>
        ) : null}
      </PersonCard>
    </CardDetail>
  );
}
